import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertOctagon, ChevronRight, BookOpen, Eye, Shield, Heart, AlertTriangle, Zap } from 'lucide-react';

const TOXIC_CONTENT = [
  {
    id: 'signs',
    title: 'Varningssignaler',
    icon: AlertTriangle,
    description: 'Kännetecken på en toxisk relation.',
    content: [
      '**Konstant kritik:** Du blir ständigt nedvärderad, förlöjligad eller kritiserad.',
      '**Kontroll:** Partnern vill veta var du är, vem du träffar, vad du gör.',
      '**Isolering:** Du pressas att dra dig undan vänner och familj.',
      '**Skuld och skam:** Du får ofta höra att allt är ditt fel.',
      '**Walking on eggshells:** Du är ständigt på din vakt för att undvika utbrott.',
      '**Gaslighting:** Du får höra att du minns fel, överreagerar eller är "galen".',
      '**Hot och ultimatum:** Partnern hotar med att lämna, skada sig själv eller dig.',
      '**Ingen respekt för gränser:** Dina "nej" ignoreras eller straffas.'
    ],
    questions: [
      'Känner du igen några av dessa mönster i din relation?',
      'Hur mår du efter att ha spenderat tid med personen?',
      'Har vänner eller familj uttryckt oro?',
      'Kan du vara dig själv i relationen?'
    ]
  },
  {
    id: 'types',
    title: 'Typer av toxiska beteenden',
    icon: Eye,
    description: 'Förstå olika former av manipulation och kontroll.',
    content: [
      '**Narcissistiskt:** Brist på empati, behov av beundran, utnyttjande av andra.',
      '**Passiv-aggressivt:** Indirekt kommunikation, tyst behandling, "jag är inte arg".',
      '**Gaslighting:** Verklighetsförvridning som får dig att tvivla på dig själv.',
      '**Love bombing:** Överväldigande kärlek i början som används för kontroll.',
      '**Hoovering:** Försök att dra tillbaka dig efter att du försökt lämna.',
      '**Triangulering:** Involvera en tredje part för att skapa avundsjuka eller osäkerhet.',
      '**DARVO:** Deny, Attack, Reverse Victim and Offender – förövarens försvarsteknik.'
    ],
    questions: [
      'Har du upplevt något av detta?',
      'Hur har det påverkat din självbild?',
      'Vad fick dig att börja tvivla på din egen verklighet?'
    ]
  },
  {
    id: 'why-stay',
    title: 'Varför det är svårt att lämna',
    icon: Heart,
    description: 'Förstå varför det inte "bara är att gå".',
    content: [
      '**Trauma bonding:** Omväxlingen mellan gott och ont skapar ett beroendeliknande band.',
      '**Intermittent reinforcement:** Sporadisk kärlek är mer beroendeframkallande än konstant.',
      '**Skam:** Du skäms för att ha "låtit det hända" eller för att andra ska döma dig.',
      '**Ekonomiskt beroende:** Du kanske inte har resurser att klara dig själv.',
      '**Barn:** Rädsla för vårdnadstvister eller för att barnen ska lida.',
      '**Rädsla:** Många toxiska relationer eskalerar när man försöker lämna.',
      '**Hopp:** Du minns den bra versionen och hoppas att den kommer tillbaka.',
      '**Normalisering:** Du kanske växt upp med liknande mönster och tror det är normalt.'
    ],
    questions: [
      'Vilka faktorer gör det svårt för dig att förändra situationen?',
      'Vad skulle behöva förändras för att du skulle känna dig trygg?',
      'Vem kan du prata med om detta?'
    ]
  },
  {
    id: 'protect',
    title: 'Skydda dig själv',
    icon: Shield,
    description: 'Strategier för att hantera och lämna.',
    content: [
      '**Dokumentera:** Spara bevis på övergrepp (meddelanden, foton, dagbok).',
      '**Bygg nätverk:** Håll kontakten med vänner och familj – bryt isoleringen.',
      '**Planera i hemlighet:** Om du planerar att lämna, berätta inte för personen.',
      '**Säkerhetsplan:** Ha en väska packad, pengar gömda, säker plats att fly till.',
      '**Sök hjälp:** Kontakta kvinnojour, Bris, eller polisen om du är i fara.',
      '**Terapi:** En terapeut kan hjälpa dig bearbeta och återbygga.',
      '**No contact:** Efter separation – ingen kontakt alls om möjligt.',
      '**Var snäll mot dig själv:** Det är inte ditt fel. Du förtjänar bättre.'
    ],
    questions: [
      'Har du en säkerhetsplan?',
      'Vem kan du ringa om du behöver fly?',
      'Har du dokumenterat det som hänt?'
    ]
  },
  {
    id: 'healing',
    title: 'Läka efter toxisk relation',
    icon: Zap,
    description: 'Vägen till återhämtning.',
    content: [
      '**Det tar tid:** Att läka är inte linjärt – förväntade inte att det ska gå fort.',
      '**No contact är nyckeln:** Varje kontakt återställer läkningsprocessen.',
      '**Terapi hjälper:** Speciellt traumafokuserad terapi (EMDR, CPT).',
      '**Lär dig om mönstren:** Kunskap om narcissism, trauma bonding etc. hjälper dig förstå.',
      '**Rebuilda din identitet:** Du förlorade dig själv – upptäck vem du är igen.',
      '**Lita på din instinkt igen:** Den var inte "galen" – du lärde dig bara att ignorera den.',
      '**Var försiktig med nya relationer:** Ta det långsamt och lär dig känna igen red flags.',
      '**Självmedkänsla:** Du överlevde – det kräver styrka.'
    ],
    questions: [
      'Vad behöver du för att läka?',
      'Hur har relationen påverkat din syn på dig själv?',
      'Vad har du lärt dig om dina gränser?'
    ]
  }
];

export default function ToxicRelationshipsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="toxic-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Toxiska relationer</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till detta innehåll.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = TOXIC_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="toxic-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Toxiska relationer</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-red-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-red-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Crisis resources */}
          <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
            <p className="text-red-800 text-sm font-semibold mb-2">🆘 Behöver du hjälp nu?</p>
            <ul className="text-red-700 text-sm space-y-1">
              <li>• <strong>Brottsofferjouren:</strong> 116 006 (alla brottsoffer)</li>
              <li>• <strong>Kvinnofridslinjen:</strong> 020-50 50 50 (kvinnor)</li>
              <li>• <strong>Mansjouren:</strong> 08-30 30 20 (män)</li>
              <li>• <strong>Bris:</strong> 116 111 (barn & unga)</li>
              <li>• <strong>Polisen:</strong> 112 (akut) / 114 14</li>
            </ul>
          </div>

          
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="toxic-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Toxiska relationer</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <AlertOctagon className="w-12 h-12 mx-auto text-red-500" />
          <h2 className="font-heading text-2xl font-semibold">Toxiska relationer</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Identifiera destruktiva mönster, skydda dig själv och hitta vägen ut.
          </p>
        </div>

        <div className="space-y-3">
          {TOXIC_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Crisis resources */}
        <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
          <p className="text-red-800 text-sm font-semibold mb-2">🆘 Om du är i fara</p>
          <ul className="text-red-700 text-sm space-y-1">
            <li>• <strong>Brottsofferjouren:</strong> 116 006</li>
            <li>• <strong>Kvinnofridslinjen:</strong> 020-50 50 50</li>
            <li>• <strong>Mansjouren:</strong> 08-30 30 20</li>
            <li>• <strong>Polisen:</strong> 112 (akut) / 114 14</li>
          </ul>
        </div>

        

        <p className="text-xs text-muted-foreground text-center px-4">
          ⚕️ Om du är i en våldsam relation, sök professionell hjälp. Du är inte ensam.
        </p>
      </main>
    </div>
  );
}
